<script>
import {defineComponent} from "vue";
import LoginStorage from '@/libs/Login';
import constants from '@/libs/Constants';
import Toast from "@/components/Toast.vue";
import Pixel from "@/components/Pixel.vue";
import Navbar from "@/components/Navbar.vue";
import plausible from "@/libs/PlausibleTracker";
import ApiConnection from "@/libs/ApiConnection";
import ModalWindow from "@/components/ModalWindow.vue";
import {isValidEmail, flagUsePixel} from "@/libs/Helpers";
import GroupErrorTranslation from "@/libs/locales/errors";
import PrivacyCookies from "@/components/PrivacyCookies.vue";

export default defineComponent({
  components: {Pixel, Toast, ModalWindow, PrivacyCookies, Navbar},
  data() {
    return {
      constants: constants,

      loading: false,
      modalLandingEmailStatus: false,
      clickedComponent: null,

      signup: {
        toast: false,
        message: '',

        //Fields
        email: '',
        full_name: '',
        institution: '',
      },
      contactList: JSON.parse(process.env.VUE_APP_ROUTING_FEEDBACK_CONTACTS),
      contactEmailSubject: process.env.VUE_APP_PRIVACY_FEEDBACK_EMAIL_SUBJECT
    }
  },
  async mounted() {
    this.signup.email = '';
    plausible.trackPageview();
  },
  methods: {
    flagUsePixel,
    onGettingStartedFromNavbar(linkName) {
      this.onGettingStartedClick(linkName)
    },
    onGettingStartedClick(goTo) {
      this.clickedComponent = goTo;

      // Verify that user has already registered
      if (LoginStorage.emailAlreadyRegistered === true) {
        this.goToClickedLink()
      } else
        this.modalLandingEmailStatus = true;
    },
    onModalLandingClose() {
      this.modalLandingEmailStatus = false;
    },
    goToClickedLink() {
      this.modalLandingEmailStatus = false;
      this.$router.push({name: this.clickedComponent});
    },
    onSignUpClick() {
      if (this.clickedComponent !== null) {
        this.loading = true;

        let fullName = this.signup.full_name.trim();
        let institution = this.signup.institution.trim();
        let email = this.signup.email.trim().toLowerCase();

        ApiConnection.putUserRegister(email, fullName, institution).then(response => {
          response.json().then(data => {
            // Register email in local session
            let emailRef = LoginStorage.registerEmail(email);

            if (emailRef !== null)
              plausible.trackEventSignup(emailRef);

            this.goToClickedLink();
          })
        }).catch(error => {
          this.signup.message = GroupErrorTranslation.t(error.message, this.$i18n);
          this.signup.toast = true;
        }).finally(() => {
          this.loading = false;
        });
      }
    }
  },
  computed: {
    isSignupButtonDisabled() {
      let email = this.signup.email.trim();
      let fullName = this.signup.full_name.trim();
      let institution = this.signup.institution.trim();

      return isValidEmail(email) === false || fullName === '' || institution === '' || this.loading === true;
    },
  }
})
</script>

<template>
  <Navbar :prop-hide-sections="false"
          @on-link-click="onGettingStartedFromNavbar"
          :prop-active-menu="constants.ROUTING.LANDING"/>
  <ModalWindow prop-id="modalLandingEmail"
               :prop-modal-footer-hide="true"
               :prop-modal-header-hide="true"
               :prop-status="modalLandingEmailStatus"
               :prop-static="true"
               prop-size="lg"
               prop-modal-header-class="border-bottom-0"
               @on-close-modal-click="onModalLandingClose">
    <template v-slot:title>&nbsp;</template>
    <div style="position: absolute; top: 15px; right: 15px;">
      <button type="button" class="btn-close" @click="onModalLandingClose" aria-label="Close"></button>
    </div>
    <div class="row px-4">
      <div class="col-lg-8 offset-lg-2 col-12 offset-0 text-center mb-3">
        <img src="@/assets/img/logos/logo.png" alt="" width="225"/>
        <div class="h5 my-2 text-blue fst-italic fw-bold">{{ $t('navbar.title') }}</div>
      </div>
      <div class="h3 text-center text-dark fw-bold border-bottom border-dark-subtle pb-3 mb-3">{{
          $t('Contact.join')
        }}
      </div>
      <div class="col-12 col-lg-6 mb-3 mb-lg-0">
        <p v-html="$t('Contact.register.fast')"></p>
        <p v-html="$t('Contact.register.why_experience')"></p>
        <p v-html="$t('Contact.register.why_security')"></p>
      </div>
      <div class="col-12 col-lg-6 my-auto">
        <div class="mb-1">
          <label for="emailUser" class="form-label">{{ $t('Contact.email') }} <span class="required">*</span></label>
          <input type="email" class="form-control" id="signupEmail" v-model="signup.email"
                 maxlength="150">
        </div>
        <div class="mb-1">
          <label for="emailUser" class="form-label">{{ $t('Contact.name') }} <span class="required">*</span></label>
          <input type="text" class="form-control" id="signupName" v-model="signup.full_name"
                 maxlength="150">
        </div>
        <div class="mb-3">
          <label for="emailUser" class="form-label">{{ $t('Contact.institution') }} <span
              class="required">*</span></label>
          <input type="text" class="form-control" id="signupInstitution"
                 v-model="signup.institution"
                 maxlength="250">
        </div>
        <div class="mb-3 text-center">
          <button class="btn btn-success btn-lg w-75" @click="onSignUpClick"
                  :disabled="this.isSignupButtonDisabled === true">
            <div v-if="loading === true" class="spinner-grow spinner-grow-sm me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <i v-else class="bi bi-box-arrow-in-right me-2"></i>
            <span>{{ $t('Contact.signup') }}</span>
          </button>
        </div>
      </div>
    </div>
  </ModalWindow>
  <Toast prop-id="toastSignupError"
         prop-type="danger"
         :prop-trigger="signup.toast"
         :prop-auto-hide="true"
         @on-toast-hidden="() => {signup.toast = false}">
    <template v-slot:body>{{ signup.message }}</template>
  </Toast>

  <!-- Landing page -->
  <main class="pb-0">
    <div class="jumbotron">
      <div class="jumbotron-overlay"></div>
      <div class="jumbotron-background"></div>
      <div class="jumbotron-container p-4 mb-4 rounded-3">
        <div class="container-fluid py-3 py-md-4">
          <div class="row">
            <div class="col-4 offset-4 col-md-3 offset-md-0 text-center text-md-end mt-2">
              <img src="@/assets/img/icons/icon-mountain-150x150.png" alt="" class="img-fluid"/>
            </div>
            <div class="col-12 col-md-8 text-center text-md-start">
              <div class="h4 text-blue fst-italic fw-bold p-2 m-0 rounded-5 d-inline-block bg-semitransparent">
                {{ $t('project.slogan') }}
              </div>
              <h1 class="fw-bolder fst-italic text-white mt-1" v-html="$t('project.title')"></h1>
              <h5 class="text-white fw-semibold fst-italic" v-html="$t('navbar.title')"></h5>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container px-5 mt-4 mb-4">
      <div class="row">
        <!-- Introduction -->
        <div class="col-12 offset-0 col-lg-8 offset-lg-2">
          <p class="fs-5 fw-light" v-html="$t('project.intro.base')"></p>
          <p class="fs-5 fw-light" v-html=" $t('project.intro.objective')"></p>
        </div>
      </div>
    </div>

    <!-- About sections -->
    <div class="px-5 py-5 mt-1 bg-secondary-subtle">
      <h2 class="pb-2 mb-4 text-blue fst-italic fw-bold">{{ $t('project.solutions.title') }}</h2>
      <div class="card-group bg-white rounded-5 py-4 px-3">
        <!-- Exploration -->
        <div class="card border-0 rounded-5">
          <div class="card-body">
            <div class="row">
              <div class="col d-flex align-items-start">
                <div
                    class="icon-square text-bg-purple-strong d-inline-flex align-items-center justify-content-center fs-4 flex-shrink-0 me-3">
                  <i class="bi bi-binoculars"></i>
                </div>
                <div>
                  <h3 class="card-title">{{ $t('navbar.visualization') }}</h3>
                  <p class="card-text fs-6 fw-light" v-html="$t('project.solutions.exploration')"></p>
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer bg-transparent border-0 text-center">
            <button class="btn btn-lg btn-purple"
                    @click="onGettingStartedClick(constants.ROUTING.VISUALIZATION)">
              <i class="bi bi-box-arrow-in-right pe-2"></i>
              <span>{{ $t('project.solutions.continue') }}</span>
            </button>
          </div>
        </div>

        <!-- Simulation -->
        <div class="card border-0 rounded-5">
          <div class="card-body">
            <div class="row">
              <div class="col d-flex align-items-start">
                <div
                    class="icon-square text-bg-purple-strong d-inline-flex align-items-center justify-content-center fs-4 flex-shrink-0 me-3">
                  <i class="bi bi-geo"></i>
                </div>
                <div>
                  <h3 class="card-title">{{ $t('navbar.dss') }}</h3>
                  <p class="card-text fs-6 fw-light" v-html="$t('project.solutions.simulation')"></p>
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer bg-transparent border-0 text-center">
            <button class="btn btn-lg btn-purple"
                    @click="onGettingStartedClick(constants.ROUTING.SIMULATION)">
              <i class="bi bi-box-arrow-in-right pe-2"></i>
              <span>{{ $t('project.solutions.continue') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="container px-5 mt-4 mb-5">
      <div class="row">
        <div class="col-12 offset-0 col-lg-8 offset-lg-2">
          <h3 class="text-blue fst-italic fw-bold">{{ $t('project.case.title') }}</h3>
          <p class="fs-5 fw-light" v-html="$t('project.case.location')"></p>
          <p class="fs-5 fw-light" v-html="$t('project.case.impact')"></p>
        </div>
      </div>
    </div>

    <!-- Contact -->
    <div class="px-5 py-5 mt-1 grey-pattern bg-secondary-subtle">
      <h2 class="pb-2 mb-2 text-blue fst-italic fw-bold">{{ $t('project.contact.title') }}</h2>
      <div class="row pt-2 px-md-3 px-0">
        <div class="col-12 mt-2 mb-4 h5">
          {{ $t('project.contact.description') }}
        </div>
        <div class="col-md-10 offset-md-1 col-12 mb-1">
          <div class="row">
            <div class="col-xl-4 col-12 mb-4 mb-xl-0" v-for="contact in contactList">
              <div class="card border-0 rounded-5">
                <div class="card-body">
                  <ul class="list-group list-group-flush">
                    <li class="list-group-item">
                      <i class="bi bi-person-circle me-2"/>
                      <span>{{ contact.name }}</span>
                    </li>
                    <li class="list-group-item">
                      <i class="bi bi-building me-2"/>
                      <span>{{ contact.company }}</span>
                    </li>
                    <li class="list-group-item">
                      <i class="bi bi-envelope me-2"/>
                      <span>
                  <a class="link-primary" :href="`mailto:${contact.email}&subject=${contactEmailSubject}`">
                    {{ contact.email }}
                  </a>
                </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-8 offset-2 my-4 text-center">
          <i18n-t keypath="project.contact.terms" tag="span">
            <template v-slot:terms_link>
              <router-link :to="{ name: constants.ROUTING.TERMS }" target="_blank">
                {{ $t('navbar.terms_of_service') }}
              </router-link>
            </template>
            <template v-slot:privacy_policy_link>
              <router-link :to="{ name: constants.ROUTING.PRIVACY }" target="_blank">
                {{ $t('navbar.privacy') }}
              </router-link>
            </template>
          </i18n-t>
        </div>
      </div>
    </div>

  </main>
  <footer class="p-5">
    <div class="row">
      <div class="col-12 col-md-4 text-center text-md-end">
        <img height="100" src="@/assets/img/logos/logo_baseline_white.png"/>
      </div>
      <div class="col-12 col-md-6">
        <div class="row mt-3">
          <div class="col-2 text-end align-content-center">
            <img height="35" src="../assets/img/flags/eu.png"/>
          </div>
          <div class="col-10">
            <span class="text-white" v-html="$t('project.funds')"></span>
          </div>
        </div>
      </div>
    </div>
  </footer>
  <!-- Cookies window -->
  <PrivacyCookies/>

  <!-- Pixel -->
  <Pixel :prop-source="constants.ROUTING.LANDING" v-if="flagUsePixel()===true"/>

</template>
<style scoped>
.jumbotron {
  height: 300px;
  width: 100%;
  margin-top: 87px;
  position: relative;

  @media (max-width: 768px) {
    margin-top: 60px;
    height: 450px;
  }
}

.jumbotron .bg-semitransparent {
  background: rgba(255, 255, 255, 0.5);
}

.jumbotron .jumbotron-overlay {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
}

.jumbotron .jumbotron-background {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  background-image: url(@/assets/img/mountains.jpg);
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
}

.jumbotron .jumbotron-container {
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: rgba(0, 0, 0, 0.2);
  top: 0;
}

.grey-pattern {
  background: url(@/assets/img/grey-pattern.png) bottom right;
  background-size: cover;
}

footer {
  background: url(@/assets/img/footer-pattern.png) bottom left, linear-gradient(60deg, #004D9B, #00A8C0F7);
  background-size: cover;
}
</style>